import { Component, ChangeDetectionStrategy, inject, signal, effect, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// Kendo UI imports
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { KENDO_PDFEXPORT, PDFExportComponent } from '@progress/kendo-angular-pdf-export';

// Distillation Funnel Component
import { EsDistillationFunnelComponent } from './components/es-distillation-funnel/es-distillation-funnel.component';

// New child components
import { EsProtectionCoverageCardsComponent } from './components/es-protection-coverage-cards/es-protection-coverage-cards.component';
import { EsGeographicThreatsComponent } from './components/es-geographic-threats/es-geographic-threats.component';
import { EsThreatCampaignChartComponent } from './components/es-threat-campaign-chart/es-threat-campaign-chart.component';

// ES Analytics Service
import { EsAnalyticsService } from './services/es-analytics.service';

// Icons
import { SVGIcon, exportIcon, filterIcon } from '@progress/kendo-svg-icons';

// Sample data imports
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, filter, forkJoin, map, of } from 'rxjs';
import { EsSecEventsBreakdownComponent } from './components/es-sec-events-breakdown/es-sec-events-breakdown.component';
import { EsTopAttackedUsersComponent } from './components/es-top-attacked-users/es-top-attacked-users.component';
import {
  EmailCoverageResponse,
  EmailFunnelResponse,
  EmailThreatsByTypeResponse,
  GeographicThreatsResponse,
  TopAttackedUsersResponse,
  EventsPendingReviewResponse,
  ConfigStatusResponse,
  EmailAverageCoverageResponse,
  ThreatCampaignResponse,
  CheckpointProtectionResponse,
  ShadowItTrendsResponse,
} from 'src/app/core/models/analytics/es-models';
import { EsEventsPendingReviewCardComponent } from './components/es-events-pending-review-card/es-events-pending-review-card.component';
import { EsConfigStatusCardComponent } from './components/es-config-status-card/es-config-status-card.component';
import { EsAverageCoverageCardComponent } from './components/es-average-coverage-card/es-average-coverage-card.component';
import { EsCpProtectionComponent } from './components/es-cp-protection/es-cp-protection.component';
import { EsShadowItTrendComponent } from './components/es-shadow-it-trend/es-shadow-it-trend.component';
import {
  AnalyticsFilterComponent,
  FilterConfig,
} from 'src/app/core/components/analytics-filter/analytics-filter.component';
import { BaseAnalyticsPageComponent } from 'src/app/core/components/base-analytics-page/base-analytics-page.component';

@Component({
  selector: 'app-es',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropDownListModule,
    ButtonModule,
    DatePickerModule,
    KENDO_PDFEXPORT,
    // Child components
    EsDistillationFunnelComponent,
    EsProtectionCoverageCardsComponent,
    EsGeographicThreatsComponent,
    EsThreatCampaignChartComponent,
    EsSecEventsBreakdownComponent,
    EsTopAttackedUsersComponent,
    EsEventsPendingReviewCardComponent,
    EsConfigStatusCardComponent,
    EsAverageCoverageCardComponent,
    EsCpProtectionComponent,
    EsShadowItTrendComponent,
    AnalyticsFilterComponent,
    BaseAnalyticsPageComponent,
  ],
  templateUrl: './es.component.html',
  styleUrl: './es.component.scss',
  providers: [EsAnalyticsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsComponent extends BaseComponent {
  @ViewChild('pdf') pdf!: PDFExportComponent;

  private esAnalyticsService = inject(EsAnalyticsService);
  private route = inject(ActivatedRoute);

  // Signal-based organization ID from route params
  public organizationId = toSignal(
    this.route.queryParams.pipe(
      map(params => params['organizationId'] ? +params['organizationId'] : null),
      filter((id): id is number => id !== null)
    ),
    { initialValue: null }
  );
  public filterIcon: SVGIcon = filterIcon;
  public startDate: Date = new Date(new Date().setDate(new Date().getDate() - 30));
  public endDate: Date = new Date();
  public startDateString: string = '';
  public endDateString: string = '';
  public Math = Math;

  public filterConfig: FilterConfig = {
    showTimeRange: true,
    showCustomerFilters: false,
    showServiceFilters: false,
    showToolFilters: false,
    showApplyButton: true,
    showResetButton: false,
  };

  // Icons
  public exportIcon: SVGIcon = exportIcon;

  // ES data signals
  public emailCoverageResponse = signal(new EmailCoverageResponse());
  public emailAverageCoverageResponse = signal(new EmailAverageCoverageResponse());
  public emailFunnelResponse = signal(new EmailFunnelResponse());
  public emailThreatsByTypeResponse = signal(new EmailThreatsByTypeResponse());
  public topAttackedUsersResponse = signal(new TopAttackedUsersResponse());
  public geographicThreatsResponse = signal(new GeographicThreatsResponse());
  public eventsPendingReviewResponse = signal(new EventsPendingReviewResponse());
  public configStatusResponse = signal(new ConfigStatusResponse());
  public threatCampaignResponse = signal(new ThreatCampaignResponse());
  public checkpointProtectionResponse = signal(new CheckpointProtectionResponse());
  public shadowItTrendsResponse = signal(new ShadowItTrendsResponse());
  // Filter properties
  public timeRanges: Array<{ text: string; value: string }> = [
    { text: 'Last 24 Hours', value: '24h' },
    { text: 'Last 7 Days', value: '7d' },
    { text: 'Last 30 Days', value: '30d' },
    { text: 'Custom', value: 'custom' },
  ];

  public selectedTimeRange: { text: string; value: string } = { text: 'Last 30 days', value: '30d' };

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect(): void {
    effect(
      () => {
        const orgId = this.organizationId();
        if (orgId) {
          this.applyFilters();
        }
      },
      { allowSignalWrites: true }
    );
  }

  // Filter methods
  public applyFilters(): void {
    const days = ({ '24h': 1, '7d': 7, '30d': 30, '90d': 90 } as Record<string, number>)[
      this.selectedTimeRange.value
    ];

    if (days) {
      this.startDate = new Date(new Date().setDate(new Date().getDate() - days));
      this.endDate = new Date();
    }

    const orgId = this.organizationId();
    if (orgId && this.startDate && this.endDate) {
      this.getAllEsData();
      this.startDateString = this.startDate.toISOString();
      this.endDateString = this.endDate.toISOString();
    }
  }

  public onTimeRangeChange(selectedRange: any): void {
    this.selectedTimeRange = selectedRange;
    // Removed applyFilters() call - it will only be called by the button now
  }

  // Update startDate when the date picker value changes
  public onStartDateChange(newDate: Date | null): void {
    if (newDate) {
      newDate.setHours(0, 0, 0, 0);
      this.startDate = newDate;
    }
    // No API call here
  }

  // Update endDate when the date picker value changes
  public onEndDateChange(newDate: Date | null): void {
    if (newDate) {
      newDate.setHours(23, 59, 59, 999);
      this.endDate = newDate;
    }
    // No API call here
  }

  private getAllEsData() {
    const params = this.buildApiParams();
    const orgId = this.organizationId();
    
    if (!orgId) return;
    
    // Parallel execution of current/status data (not affected by time filters)
    const currentDataCalls = forkJoin({
      emailCoverage: this.esAnalyticsService.getEmailCoverageData(orgId, params),
      emailAverageCoverage: this.esAnalyticsService.getEmailAverageCoverageData(orgId, params),
      eventsPendingReview: this.esAnalyticsService.getEventsPendingReviewData(orgId, params),
      configStatus: this.esAnalyticsService.getConfigStatusData(orgId, params),
    });

    // Parallel execution of time-filtered historical data
    const historicalDataCalls = forkJoin({
      emailFunnel: this.esAnalyticsService
        .getEmailFunnelData(orgId, params)
        .pipe(catchError(() => of(new EmailFunnelResponse()))),
      emailThreatsByType: this.esAnalyticsService
        .getEmailThreatsByTypeData(orgId, params)
        .pipe(catchError(() => of(new EmailThreatsByTypeResponse()))),
      topAttackedUsers: this.esAnalyticsService
        .getTopAttackedUsersData(orgId, params)
        .pipe(catchError(() => of(new TopAttackedUsersResponse()))),
      threatCampaign: this.esAnalyticsService
        .getThreatCampaignData(orgId, params)
        .pipe(catchError(() => of(new ThreatCampaignResponse()))),
      geographicThreats: this.esAnalyticsService.getGeographicThreatsData(orgId, params),
      checkpointProtection: this.esAnalyticsService
        .getCheckpointProtectionData(orgId, params)
        .pipe(catchError(() => of(new CheckpointProtectionResponse()))),
      shadowItTrends: this.esAnalyticsService
        .getShadowItTrendsData(orgId, params)
        .pipe(catchError(() => of(new ShadowItTrendsResponse()))),
    });

    // Execute both groups in parallel and update signals when complete
    forkJoin({
      current: currentDataCalls,
      historical: historicalDataCalls
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (responses) => {
        // Update current data signals
        this.emailCoverageResponse.set(responses.current.emailCoverage);
        this.emailAverageCoverageResponse.set(responses.current.emailAverageCoverage);
        this.eventsPendingReviewResponse.set(responses.current.eventsPendingReview);
        this.configStatusResponse.set(responses.current.configStatus);

        // Update historical data signals
        this.emailFunnelResponse.set(responses.historical.emailFunnel);
        this.emailThreatsByTypeResponse.set(responses.historical.emailThreatsByType);
        this.topAttackedUsersResponse.set(responses.historical.topAttackedUsers);
        this.threatCampaignResponse.set(responses.historical.threatCampaign);
        this.geographicThreatsResponse.set(responses.historical.geographicThreats);
        this.checkpointProtectionResponse.set(responses.historical.checkpointProtection);
        this.shadowItTrendsResponse.set(responses.historical.shadowItTrends);
      },
      error: (error) => {
        console.error('Error fetching ES analytics data:', error);
      }
    });
  }

  private buildApiParams(): any {
    const params: any = {};

    if (this.startDate) {
      params.current_start = this.startDate.toISOString();
    }

    if (this.endDate) {
      params.current_end = this.endDate.toISOString();
    }

    if (this.selectedTimeRange.value !== 'custom') {
      params.time_range = this.selectedTimeRange.value;
    }

    return params;
  }


  public onParentPdfExport(filename: string): void {
    console.log('ES component received PDF export request with filename:', filename);
    if (this.pdf) {
      this.pdf.saveAs(filename);
    } else {
      console.error('ES PDF export component not found');
    }
  }

  // TEMPORARY: Handle configuration changes from child component (for prototyping with mock data)
  // TODO: Remove when real API is implemented - normal data refresh will handle updates
  public onConfigStatusChanged(): void {
    const orgId = this.organizationId();
    if (!orgId) return;
    
    // Refresh only config status data
    this.esAnalyticsService.getConfigStatusData(orgId, this.buildApiParams())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => this.configStatusResponse.set(data),
        error: (error) => console.error('Error fetching config status:', error),
      });
  }
}
